export interface StoreInfo {
  businessName: string
  address: {
    street: string
    city: string
    state: string
    zipCode: string
    description?: string
  }
  contact: {
    phone: string
    email: string
    website: string
  }
  hours: {
    monday: string
    tuesday: string
    wednesday: string
    thursday: string
    friday: string
    saturday: string
    sunday: string
  }
  social: {
    facebook?: string
    instagram?: string
    twitter?: string
  }
  description: string
}

// Default store information - this will be the source of truth
const defaultStoreInfo: StoreInfo = {
  businessName: "The Salty Mermaid Art Studio & Gallery",
  address: {
    street: "1546 Ocean Shore Blvd",
    city: "Ormond Beach",
    state: "FL",
    zipCode: "32176",
    description: "Beautiful oceanside location!"
  },
  contact: {
    phone: "(904) 704-7449",
    email: "thesaltymermaidart@gmail.com",
    website: "saltymermaidart.com"
  },
  hours: {
    monday: "Closed",
    tuesday: "10:00 AM - 4:00 PM",
    wednesday: "10:00 AM - 4:00 PM", 
    thursday: "10:00 AM - 4:00 PM",
    friday: "10:00 AM - 4:00 PM",
    saturday: "10:00 AM - 4:00 PM",
    sunday: "Closed"
  },
  social: {
    facebook: "https://www.facebook.com/TheSaltyMermaidArt/"
  },
  description: "Coastal-inspired art classes and gallery featuring original works from over 75 talented local artists. Located right across from the beach in Ormond Beach, Florida."
}

class StoreInfoService {
  private storeInfo: StoreInfo
  private listeners: (() => void)[] = []
  private isLoaded: boolean = false

  constructor() {
    // Initialize with defaults first
    this.storeInfo = { ...defaultStoreInfo }

    // Then attempt to load from JSON file (async)
    this.loadFromJson()
  }

  /**
   * Loads store information from the static JSON file.
   * This is the authoritative source of truth for business hours.
   * Falls back to localStorage if JSON fails to load.
   */
  private async loadFromJson(): Promise<void> {
    try {
      const response = await fetch('/data/store-info.json')
      if (!response.ok) {
        throw new Error(`Failed to fetch store info: ${response.status}`)
      }
      const jsonData: StoreInfo = await response.json()

      // Update with JSON data
      this.storeInfo = { ...jsonData }
      this.isLoaded = true

      // Also update localStorage to keep it in sync
      localStorage.setItem('storeInfo', JSON.stringify(this.storeInfo))

      // Notify all components to re-render with the loaded data
      this.notifyListeners()

      console.log('Store info loaded from JSON file')
    } catch (error) {
      console.warn('Failed to load store info from JSON, falling back to localStorage:', error)

      // Fallback to localStorage if JSON fails
      const stored = localStorage.getItem('storeInfo')
      if (stored) {
        this.storeInfo = JSON.parse(stored)
        this.isLoaded = true
        this.notifyListeners()
        console.log('Store info loaded from localStorage')
      } else {
        // Already using defaults from constructor
        this.isLoaded = true
        console.log('Using default store info')
      }
    }
  }

  getStoreInfo(): StoreInfo {
    return { ...this.storeInfo }
  }

  updateStoreInfo(updates: Partial<StoreInfo>): void {
    this.storeInfo = { ...this.storeInfo, ...updates }
    localStorage.setItem('storeInfo', JSON.stringify(this.storeInfo))
    this.notifyListeners()
  }

  subscribe(listener: () => void): () => void {
    this.listeners.push(listener)
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener)
    }
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener())
  }

  // Utility methods for common formatting needs
  getFormattedAddress(): string {
    const addr = this.storeInfo.address
    return `${addr.street}\n${addr.city}, ${addr.state} ${addr.zipCode}`
  }

  getFormattedAddressOneLine(): string {
    const addr = this.storeInfo.address
    return `${addr.street}, ${addr.city}, ${addr.state} ${addr.zipCode}`
  }

  getFormattedPhone(): string {
    return this.storeInfo.contact.phone
  }

  getFormattedEmail(): string {
    return this.storeInfo.contact.email
  }

  getBusinessHours(): Array<{day: string, hours: string}> {
    const daysOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
    const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    
    return daysOrder.map((day, index) => ({
      day: dayNames[index],
      hours: this.storeInfo.hours[day as keyof typeof this.storeInfo.hours]
    }))
  }

  getWorkingDaysHours(): Array<{days: string, hours: string}> {
    const hours = this.getBusinessHours()
    const grouped: Array<{days: string, hours: string}> = []
    
    // Group consecutive days with same hours
    let currentGroup: {days: string[], hours: string} | null = null
    
    hours.forEach(({ day, hours: dayHours }) => {
      if (!currentGroup || currentGroup.hours !== dayHours) {
        // Start new group
        if (currentGroup) {
          grouped.push({
            days: currentGroup.days.length === 1 ? currentGroup.days[0] : `${currentGroup.days[0]} - ${currentGroup.days[currentGroup.days.length - 1]}`,
            hours: currentGroup.hours
          })
        }
        currentGroup = { days: [day], hours: dayHours }
      } else {
        // Add to current group
        currentGroup.days.push(day)
      }
    })
    
    // Add final group
    if (currentGroup) {
      grouped.push({
        days: currentGroup.days.length === 1 ? currentGroup.days[0] : `${currentGroup.days[0]} - ${currentGroup.days[currentGroup.days.length - 1]}`,
        hours: currentGroup.hours
      })
    }
    
    return grouped
  }

  // Reset to defaults
  resetToDefaults(): void {
    this.storeInfo = { ...defaultStoreInfo }
    localStorage.setItem('storeInfo', JSON.stringify(this.storeInfo))
    this.notifyListeners()
  }
}

// Export singleton instance
export const storeInfoService = new StoreInfoService()